import { generatedCategories } from '../../data/categories';
import axios from '../../axios';
import {
  mapJioSaavnAlbumToSpotifyAlbum,
  mapJioSaavnPlaylistToSpotifyPlaylist,
  mapJioSaavnSongToSpotifyTrack,
} from '../../utils/jiosaavn-adapters';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Constants
import {
  TRENDING_URI,
  MADE_FOR_YOU_URI,
  RANKING_URI,
} from '../../constants/spotify';

// Interfaces
import type { Playlist } from '../../interfaces/playlists';
import type { Category } from '../../interfaces/categories';
import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';

const initialState: {
  category: Category | null;
  playlists: Playlist[];
  tracks: Track[];
  albums: Album[];
  loading: boolean;
} = {
  category: null,
  playlists: [],
  tracks: [],
  albums: [],
  loading: true,
};

export const fetchGenre = createAsyncThunk<[Category, Playlist[], Track[], Album[]], string>(
  'genre/fetchGenre',
  async (id, thunkAPI) => {
    const cleanId = (id || '').trim();
    const lowerId = cleanId.toLowerCase();

    const rootState: any = thunkAPI.getState();
    const homeState = rootState?.home;
    const authUser = rootState?.auth?.user;

    // 1. Detect special sections & Spotify URI constants
    const isTrending =
      lowerId === 'trending-songs' ||
      lowerId === 'trending' ||
      cleanId === TRENDING_URI ||
      lowerId === TRENDING_URI.toLowerCase() ||
      lowerId.includes('0jq5daqbmkfq');

    const isMadeForYou =
      lowerId === 'made-for-you' ||
      cleanId === MADE_FOR_YOU_URI ||
      lowerId === MADE_FOR_YOU_URI.toLowerCase() ||
      lowerId.includes('0jq5dat0tbj');

    const isCharts =
      lowerId === 'charts' ||
      lowerId === 'rankings' ||
      cleanId === RANKING_URI ||
      lowerId === RANKING_URI.toLowerCase() ||
      lowerId.includes('0jq5daudknj');

    const isTopMixes = lowerId === 'top-mixes' || lowerId === 'mixes';
    const isFeaturedPlaylists = lowerId === 'featured-playlists';
    const isNewReleases = lowerId === 'new-releases';
    const isTopTracks = lowerId === 'top-tracks' || lowerId === 'popular-tracks';

    let category: Category | null = null;
    let playlists: Playlist[] = [];
    let tracks: Track[] = [];
    let albums: Album[] = [];

    // --- CASE 1: Trending & Trending Songs ---
    if (isTrending) {
      const isSongsSpecific = lowerId === 'trending-songs' || lowerId.includes('0jq5daqbmkfq');

      if (isSongsSpecific) {
        if (homeState?.trendingSongs && homeState.trendingSongs.length > 0) {
          tracks = homeState.trendingSongs;
        } else {
          try {
            const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
            const rawSongs = (modRes.data?.data?.new_trending || []).filter((item: any) => item.type === 'song');
            tracks = rawSongs.map(mapJioSaavnSongToSpotifyTrack);
          } catch (e) {}

          if (!tracks.length) {
            try {
              const sRes = await axios.get<any>('/search/songs', { params: { query: 'Trending Hindi', limit: 40 } });
              tracks = (sRes.data?.data?.results || []).map(mapJioSaavnSongToSpotifyTrack);
            } catch (e) {}
          }
        }

        category = {
          id: cleanId,
          name: 'Trending Songs',
          href: '',
          icons: [{ url: tracks[0]?.album?.images?.[0]?.url || '', width: 300, height: 300 }],
        };

        return [category, [], tracks, []];
      } else {
        // Jump back in
        if (homeState?.trending && homeState.trending.length > 0) {
          playlists = homeState.trending;
        } else {
          try {
            const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
            const raw = (modRes.data?.data?.new_trending || []).filter((item: any) => item.type === 'playlist');
            playlists = raw.map(mapJioSaavnPlaylistToSpotifyPlaylist);
          } catch (e) {}
        }

        category = {
          id: cleanId,
          name: 'Jump back in',
          href: '',
          icons: [{ url: playlists[0]?.images?.[0]?.url || '', width: 300, height: 300 }],
        };

        return [category, playlists, [], []];
      }
    }

    // --- CASE 2: Made For You / Made For Local User ---
    if (isMadeForYou) {
      if (homeState?.madeForYou && homeState.madeForYou.length > 0) {
        playlists = homeState.madeForYou;
      } else {
        try {
          const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
          if (modRes.data?.data?.top_playlists?.length) {
            playlists = modRes.data.data.top_playlists.slice(0, 18).map(mapJioSaavnPlaylistToSpotifyPlaylist);
          }
        } catch (e) {}
      }

      const categoryName = authUser?.display_name ? `Made For ${authUser.display_name}` : 'Made For You';

      category = {
        id: cleanId,
        name: categoryName,
        href: '',
        icons: [
          {
            url:
              playlists[0]?.images?.[0]?.url ||
              'https://images.unsplash.com/photo-1614680376593-902f74cf0d41?w=274&h=274&fit=crop',
            width: 274,
            height: 274,
          },
        ],
      };

      return [category, playlists, [], []];
    }

    // --- CASE 3: Charts & Rankings ---
    if (isCharts) {
      if (homeState?.rankings && homeState.rankings.length > 0) {
        playlists = homeState.rankings;
      } else {
        try {
          const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
          if (modRes.data?.data?.charts?.length) {
            playlists = modRes.data.data.charts.map(mapJioSaavnPlaylistToSpotifyPlaylist);
          }
        } catch (e) {}
      }

      category = {
        id: cleanId,
        name: 'Featured Charts',
        href: '',
        icons: [
          {
            url: playlists[0]?.images?.[0]?.url || 'https://c.saavncdn.com/editorial/charts_500x500.jpg',
            width: 300,
            height: 300,
          },
        ],
      };

      return [category, playlists, [], []];
    }

    // --- CASE 4: Top Mixes ---
    if (isTopMixes) {
      if (homeState?.featurePlaylists && homeState.featurePlaylists.length > 0) {
        playlists = homeState.featurePlaylists;
      } else {
        try {
          const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
          const list = modRes.data?.data?.top_playlists || [];
          playlists = list.slice(12, 36).map(mapJioSaavnPlaylistToSpotifyPlaylist);
        } catch (e) {}
      }

      category = {
        id: cleanId,
        name: 'Your top mixes',
        href: '',
        icons: [
          {
            url:
              playlists[0]?.images?.[0]?.url ||
              'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=274&h=274&fit=crop',
            width: 274,
            height: 274,
          },
        ],
      };

      return [category, playlists, [], []];
    }

    // --- CASE 5: Featured Playlists ---
    if (isFeaturedPlaylists) {
      if (homeState?.featurePlaylists && homeState.featurePlaylists.length > 0) {
        playlists = homeState.featurePlaylists;
      } else {
        try {
          const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
          const list = modRes.data?.data?.top_playlists || [];
          playlists = list.slice(12, 40).map(mapJioSaavnPlaylistToSpotifyPlaylist);
        } catch (e) {}
      }

      category = {
        id: cleanId,
        name: 'Featured Playlists',
        href: '',
        icons: [
          {
            url:
              playlists[0]?.images?.[0]?.url ||
              'https://c.saavncdn.com/editorial/logo/TaazaTunes_20260601104008_500x500.jpg',
            width: 300,
            height: 300,
          },
        ],
      };

      return [category, playlists, [], []];
    }

    // --- CASE 6: New Releases / Albums featuring songs you like ---
    if (isNewReleases) {
      if (homeState?.newReleases && homeState.newReleases.length > 0) {
        albums = homeState.newReleases;
      } else {
        try {
          const modRes = await axios.get<any>('/modules', { params: { language: 'hindi,english' } });
          const list = (modRes.data?.data?.new_albums || []).filter((item: any) => item.type === 'album');
          albums = list.map(mapJioSaavnAlbumToSpotifyAlbum);
        } catch (e) {}
      }

      category = {
        id: cleanId,
        name: 'Albums featuring songs you like',
        href: '',
        icons: [
          {
            url:
              albums[0]?.images?.[0]?.url ||
              'https://c.saavncdn.com/editorial/logo/TaazaTunes_20260601104008_500x500.jpg',
            width: 300,
            height: 300,
          },
        ],
      };

      return [category, [], [], albums];
    }

    // --- CASE 7: Popular tracks ---
    if (isTopTracks) {
      if (homeState?.topTracks && homeState.topTracks.length > 0) {
        tracks = homeState.topTracks;
      }
      category = {
        id: cleanId,
        name: 'Popular tracks',
        href: '',
        icons: [{ url: tracks[0]?.album?.images?.[0]?.url || '', width: 300, height: 300 }],
      };
      return [category, [], tracks, []];
    }

    // --- CASE 8: Any Category from Browse / Search or Dynamic Fallback ---
    let matchedCat = generatedCategories.find((c) => c.id === cleanId) || null;
    if (!matchedCat) {
      matchedCat =
        generatedCategories.find(
          (c) =>
            c.id.toLowerCase() === lowerId ||
            c.name.toLowerCase() === lowerId ||
            c.name.toLowerCase() === lowerId.replace(/-/g, ' ')
        ) || null;
    }

    const categoryName = matchedCat
      ? matchedCat.name
      : decodeURIComponent(cleanId)
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());

    // Search playlists for categoryName
    try {
      const response = await axios.get<any>('/search/playlists', {
        params: { query: categoryName, limit: 50 },
      });
      const results = response.data?.data?.results || [];
      playlists = results.map(mapJioSaavnPlaylistToSpotifyPlaylist);
    } catch (e) {
      console.error('Failed to fetch playlists for category', e);
    }

    // If playlists is empty, fallback to searching songs
    if (!playlists.length) {
      try {
        const songRes = await axios.get<any>('/search/songs', {
          params: { query: categoryName, limit: 50 },
        });
        const songResults = songRes.data?.data?.results || [];
        tracks = songResults.map(mapJioSaavnSongToSpotifyTrack);
      } catch (e) {}
    }

    const finalIcon =
      matchedCat?.icons?.[0]?.url ||
      playlists[0]?.images?.[0]?.url ||
      tracks[0]?.album?.images?.[0]?.url ||
      'https://c.saavncdn.com/editorial/logo/TaazaTunes_20260601104008_500x500.jpg';

    category = {
      id: cleanId,
      name: categoryName,
      href: '',
      icons: finalIcon ? [{ url: finalIcon, width: 300, height: 300 }] : [],
    };

    return [category, playlists, tracks, albums];
  }
);

const genreSlice = createSlice({
  name: 'genre',
  initialState,
  reducers: {
    setGenre: (state, action) => {
      state.category = action.payload;
      if (!action.payload) {
        state.playlists = [];
        state.tracks = [];
        state.albums = [];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGenre.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchGenre.fulfilled, (state, action) => {
      state.category = action.payload[0];
      state.playlists = action.payload[1] || [];
      state.tracks = action.payload[2] || [];
      state.albums = action.payload[3] || [];
      state.loading = false;
    });
    builder.addCase(fetchGenre.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const genreActions = {
  fetchGenre,
  ...genreSlice.actions,
};

export default genreSlice.reducer;
